import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Member } from "../../features/members/models/Member";

interface MemberSearchSelectProps {
  members: Member[];
  value: string;
  onChange: (memberId: string) => void;
  placeholder?: string;
}

const MAX_RESULTS = 8;

/**
 * Search-as-you-type member picker: type to filter, click to select; the selected
 * member shows as a clearable chip-style field.
 */
const MemberSearchSelect: React.FC<MemberSearchSelectProps> = ({
  members,
  value,
  onChange,
  placeholder,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = members.find((m) => m.id === value);

  const candidates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return members
      .filter((m) => {
        if (!needle) return true;
        const name = `${m.firstName} ${m.lastName}`.toLowerCase();
        return name.includes(needle) || (m.email?.toLowerCase().includes(needle) ?? false);
      })
      .slice(0, MAX_RESULTS);
  }, [members, search]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      {selected ? (
        <div className="input flex items-center justify-between gap-2">
          <span className="truncate">{selected.firstName} {selected.lastName}</span>
          <button
            type="button"
            onClick={() => { onChange(""); setSearch(""); setOpen(true); }}
            className="shrink-0 rounded-md p-0.5 text-grey-40 hover:bg-grey-95 hover:text-grey-20 dark:text-grey-60 dark:hover:bg-primary-20 dark:hover:text-white"
            aria-label={t("billing.memberSearch.clear")}
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            className="input w-full pr-8"
            placeholder={placeholder ?? t("billing.memberSearch.placeholder")}
            value={search}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          />
          <ChevronDown
            size={16}
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-grey-40 dark:text-grey-60"
          />
        </div>
      )}

      {open && !selected && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-grey-90 bg-white p-1 shadow-lg dark:border-primary-20 dark:bg-primary-10">
          {candidates.length === 0 ? (
            <p className="px-3 py-1.5 text-sm text-grey-40 dark:text-grey-60">{t("billing.memberSearch.noMatches")}</p>
          ) : (
            candidates.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => { onChange(m.id); setOpen(false); }}
                className="block w-full rounded-lg px-3 py-1.5 text-left text-sm text-grey-10 hover:bg-grey-95 dark:text-grey-90 dark:hover:bg-primary-20"
              >
                {m.firstName} {m.lastName}
                {m.email && <span className="ml-2 text-xs text-grey-40 dark:text-grey-60">{m.email}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MemberSearchSelect;
